import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { ProgressIndicator } from "@/components/ui/progress-indicator";
import { RightSidebar } from "@/components/layout/right-sidebar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { SessionSwitcher } from "@/components/ui/session-switcher";
import { MobileSidebarTrigger, MobileSidebar } from "./mobile-sidebar";
import { NAVIGATION_DATA } from "@/config/navigation";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGetEntityData } from "@/hooks/api/query/useGetEntityData";
import { useAuth } from "@/contexts/AuthContext";

import { ConditionalStep, SubStep } from "@/types/wizard";
import { cn } from "../../lib/utils";
import { useSidebar } from "../ui/sidebar";

interface WizardLayoutProps {
  children: ReactNode;
  title: string;
  description?: string;
  currentStep: number;
  steps: string[];
  currentSubSteps?: SubStep[];
  conditionalSteps?: ConditionalStep[];
  conditionalStepSubSteps?: SubStep[];
  onBack?: () => void;
  onStepClick?: (stepIndex: number) => void;
  showBackButton?: boolean;
  className?: string;
  fullWidth?: boolean;
  userAvatar?: ReactNode;
  cardClassName?: string;
  footer?: ReactNode;
}

export function WizardLayout({
  children,
  title,
  description,
  currentStep,
  steps,
  currentSubSteps,
  conditionalSteps,
  conditionalStepSubSteps,
  onBack,
  onStepClick,
  showBackButton = false,
  className,
  fullWidth = false,
  userAvatar,
  cardClassName,
  footer,
}: WizardLayoutProps) {
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { currentSessionId } = useSessionSwitcher();

  const { data: entityDataResponse } = useGetEntityData(currentSessionId, {
    enabled: !!currentSessionId && !!user,
    retry: 1,
  });

  const entityData = entityDataResponse?.entity_data;

  const { open: isSidebarOpen } = useSidebar();

  const hasSubSteps =
    currentSubSteps?.length || conditionalStepSubSteps?.length;
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-navy-900 py-2 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {/* Left - Mobile Menu Button */}
            <div className="w-10">
              {isMobile && (
                <MobileSidebarTrigger
                  onClick={() => setIsMobileSidebarOpen(true)}
                />
              )}
            </div>

            {/* Center - Logo/Title */}
            {isMobile ? (
              <Link to="/" className="text-sm font-heading font-bold text-white">
                NIS2PASS
              </Link>
            ) : (
              <h1 className="text-sm font-heading font-bold text-white">
                Asistent inteligent pentru încadrarea în domeniul de aplicare al
                NIS2 (OUG 155/2024)
              </h1>
            )}

            {/* Right - User Avatar or spacer */}
            <div className="w-10 flex justify-end">{userAvatar}</div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      {isMobile && (
        <MobileSidebar
          isOpen={isMobileSidebarOpen}
          onOpenChange={setIsMobileSidebarOpen}
          navigationData={NAVIGATION_DATA}
          entityData={entityData}
          sessionId={currentSessionId}
          isAuthenticated={!!user}
          userEmail={user?.email}
          sessionSwitcherSlot={
            user ? <SessionSwitcher entityData={entityData} /> : null
          }
        />
      )}

      {/* Progress Indicator */}
      <div
        className={cn(
          "bg-white border-b",
          isMobile
            ? "max-w-[100vw]"
            : isSidebarOpen
              ? `max-w-[calc(100vw-256px)]`
              : `max-w-[calc(100vw-48px)]`
        )}
      >
        <div className="container mx-auto md:px-4 px-2">
          <ProgressIndicator
            steps={steps}
            currentStep={currentStep}
            currentSubSteps={currentSubSteps}
            conditionalSteps={conditionalSteps}
            conditionalStepSubSteps={conditionalStepSubSteps}
            onStepClick={onStepClick}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 min-h-0">
        <main
          className={cn(
            "flex-1 py-8 overflow-y-auto relative",
            footer && "pb-24"
          )}
        >
          {/* Floating Progress Toggle Button */}
          {hasSubSteps && !isMobile && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSidebarVisible(!isSidebarVisible)}
              className="fixed top-20 right-4 z-50 shadow-lg bg-background border-border hover:bg-muted"
            >
              {isSidebarVisible ? (
                <>
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Ascunde progres
                </>
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Progres pas curent
                </>
              )}
            </Button>
          )}

          <div className="container mx-auto px-0 md:px-4">
            <div className={cn("mx-auto", fullWidth ? "w-full" : "max-w-4xl")}>
              <Card className={cn("shadow-form", cardClassName)}>
                <CardContent className={className}>{children}</CardContent>
              </Card>
            </div>
          </div>
        </main>
        {footer && (
          <div
            className={cn(
              "fixed bottom-0 right-0 z-20 bg-gradient-to-t from-background via-background to-transparent pt-6 pb-[calc(1rem+env(safe-area-inset-bottom))] px-3 md:px-4 transition-all duration-200",
              isMobile
                ? "left-0"
                : isSidebarOpen
                  ? "left-[256px]"
                  : "left-[48px]"
            )}
          >
            <div className={cn(!isMobile && "container mx-auto")}>
              <div
                className={cn(
                  !isMobile && "mx-auto",
                  !isMobile && (fullWidth ? "w-full" : "max-w-4xl")
                )}
              >
                <div className="flex items-center justify-between gap-4 bg-white border border-ds-border-200 rounded-xl p-3 md:p-4 shadow-lg">
                  {footer}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Right Sidebar for Sub-steps */}
        <RightSidebar
          currentSubSteps={currentSubSteps}
          conditionalStepSubSteps={conditionalStepSubSteps}
          currentStepName={steps[currentStep]}
          className={cn(
            "transition-all duration-300 ease-in-out",
            isSidebarVisible ? "flex" : "hidden"
          )}
        />
      </div>
    </div>
  );
}
